/**
 * TODO 7.2 — Step5Pricing.jsx
 * 
 * Tela 5 — Preço e Duração dos Serviços (tela única — D1)
 * Lista todos os serviços com campos de preço (opcional) e duração (radio buttons)
 * 
 * Props:
 *   wizard = useWelcomeWizard()
 * 
 * Estado guardado em wizard.state.serviceDetails = { [serviceId]: { price, durationMinutes } }
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Stack,
  Divider,
  Paper,
  InputAdornment,
} from '@mui/material';
import { getWelcomeText } from '../../../utils/welcomeText';
import { wlog } from '../../../utils/welcomeDebug';
import { formatCurrency } from '../../../utils/formatters';

const DURATION_OPTIONS = [
  { label: '15 min', value: 15 },
  { label: '30 min', value: 30 },
  { label: '1 hora', value: 60 },
  { label: '1h30', value: 90 },
  { label: '2 horas', value: 120 },
  { label: 'Outro', value: 'custom' },
];

export default function Step5Pricing({ wizard, goNext }) {
  const { template, state } = wizard;
  wlog('Step5Pricing', 'RENDER', {
    templateId: template?.id || 'NENHUM',
    servicesLen: state?.services?.length,
  });
  const services = state.services || [];

  // Inicializa os detalhes de cada serviço
  const [serviceFormData, setServiceFormData] = useState({});
  const [customDurations, setCustomDurations] = useState({});

  useEffect(() => {
    // Inicializa com dados existentes ou defaults
    const initialized = {};
    services.forEach((svc) => {
      initialized[svc.id] = {
        price: state.serviceDetails?.[svc.id]?.price ?? null,
        duration: state.serviceDetails?.[svc.id]?.durationMinutes ?? 60,
      };
    });
    setServiceFormData(initialized);
  }, [services, state.serviceDetails]);

  // Textos temáticos
  const [titleText, setTitleText] = useState('');
  const [hintText, setHintText] = useState('');

  useEffect(() => {
    const loadText = async () => {
      const title = await getWelcomeText(template, 'pricingTitle');
      const hint = await getWelcomeText(template, 'pricingHint');
      setTitleText(title);
      setHintText(hint);
    };
    loadText().catch(console.error);
  }, [template]);

  // Atualiza preço
  const handlePriceChange = (serviceId, value) => {
    const numValue = value === '' ? null : parseFloat(value) || null;
    setServiceFormData((prev) => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        price: numValue,
      },
    }));
  };

  // Atualiza duração (radio buttons)
  const handleDurationChange = (serviceId, value) => {
    if (value === 'custom') {
      setServiceFormData((prev) => ({
        ...prev,
        [serviceId]: {
          ...prev[serviceId],
          duration: customDurations[serviceId] ?? 60,
        },
      }));
    } else {
      const numValue = parseInt(value);
      setServiceFormData((prev) => ({
        ...prev,
        [serviceId]: {
          ...prev[serviceId],
          duration: numValue,
        },
      }));
      setCustomDurations((prev) => ({
        ...prev,
        [serviceId]: numValue,
      }));
    }
  };

  // Atualiza duração customizada
  const handleCustomDurationChange = (serviceId, value) => {
    const numValue = parseInt(value) || 60;
    setCustomDurations((prev) => ({
      ...prev,
      [serviceId]: numValue,
    }));
    setServiceFormData((prev) => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        duration: numValue,
      },
    }));
  };

  // Determina qual radio está selecionado para cada serviço
  const getSelectedDurationOption = (serviceId) => {
    const duration = serviceFormData[serviceId]?.duration;
    const predefined = DURATION_OPTIONS.find(
      (opt) => opt.value !== 'custom' && opt.value === duration
    );
    return predefined ? predefined.value : 'custom';
  };

  // Continuar
  const handleContinue = () => {
    // Salva todos os detalhes
    services.forEach((svc) => {
      const data = serviceFormData[svc.id];
      wizard.setServiceDetail(svc.id, {
        price: data.price,
        durationMinutes: data.duration,
      });
    });
    wizard.goNext();
  };

  const colors = wizard.template?.brand?.colors || {}; // Welcome veste a MARCA do ramo (D16)

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        backgroundColor: colors.surface || colors.background || '#f9f9f9',
      }}
    >
      <Container maxWidth="sm">
        <section>
          {/* Título temático */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              marginBottom: '8px',
              textAlign: 'center',
              color: colors.text || '#000',
            }}
          >
            {titleText}
          </Typography>

          {/* Aviso de D1 (obrigatório) */}
          <Paper
            sx={{
              backgroundColor: '#fffacd',
              border: `1px solid ${colors.warning || '#ffd700'}`,
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '24px',
              marginTop: '16px',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: colors.warning || '#856404',
                lineHeight: 1.6,
              }}
            >
              💡 {hintText}
            </Typography>
          </Paper>

          {/* Listagem de serviços */}
          <Stack spacing={3}>
            {services.map((service, idx) => {
              const data = serviceFormData[service.id] || {};
              const selectedDuration = getSelectedDurationOption(service.id);
              const isCustom = selectedDuration === 'custom';

              return (
                <Paper
                  key={service.id}
                  sx={{
                    padding: '16px',
                    backgroundColor: colors.surface || '#fff',
                    border: `1px solid ${colors.divider || 'rgba(0,0,0,0.1)'}`,
                    borderRadius: '8px',
                  }}
                >
                  {/* Nome do serviço */}
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      marginBottom: '16px',
                      color: colors.text || '#000',
                    }}
                  >
                    {service.name}
                  </Typography>

                  {/* Preço */}
                  <Box sx={{ marginBottom: '16px' }}>
                    <TextField
                      fullWidth
                      label="Preço (opcional)"
                      type="number"
                      inputMode="decimal"
                      placeholder="0,00"
                      value={data.price ?? ''}
                      onChange={(e) => handlePriceChange(service.id, e.target.value)}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">R$</InputAdornment>
                        ),
                      }}
                      sx={{
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                        '& input[type=number]::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                      }}
                    />
                  </Box>

                  {/* Duração */}
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        marginBottom: '8px',
                        color: colors.text || '#000',
                      }}
                    >
                      Duração
                    </Typography>
                    <RadioGroup
                      row
                      value={selectedDuration}
                      onChange={(e) =>
                        handleDurationChange(service.id, e.target.value)
                      }
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                        marginBottom: '12px',
                      }}
                    >
                      {DURATION_OPTIONS.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={<Radio size="small" />}
                          label={option.label}
                          sx={{
                            margin: '0',
                            '& .MuiFormControlLabel-label': {
                              fontSize: '13px',
                            },
                          }}
                        />
                      ))}
                    </RadioGroup>

                    {/* Campo customizado para duração */}
                    {isCustom && (
                      <TextField
                        fullWidth
                        label="Duração em minutos"
                        type="number"
                        value={customDurations[service.id] ?? 60}
                        onChange={(e) =>
                          handleCustomDurationChange(service.id, e.target.value)
                        }
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">min</InputAdornment>
                          ),
                        }}
                      />
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Stack>

          {/* Botão Continuar */}
          <Box sx={{ marginTop: '32px', textAlign: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleContinue}
              sx={{
                textTransform: 'none',
                minWidth: '120px',
                padding: '10px 24px',
              }}
            >
              Continuar
            </Button>
          </Box>
        </section>
      </Container>
    </Box>
  );
}
